/**
 * File types that can be sent to the model.
 *
 * Anything attached to a policy or a chat message is passed to Gemini, so the
 * accepted set is bounded by what it can actually read — not by what looks
 * plausible in a file picker. `.docx` and `.xlsx` are safe because the backend
 * (services/workflow/utils/document-text.ts) extracts their text/table content
 * up front instead of handing the raw zip archive to the model; legacy binary
 * Office formats (`.doc`, `.xls`, `.ppt`, `.pptx`) have no such extractor, so
 * they'd still upload happily and extract as noise.
 */

/** Extensions offered in the file picker, and enforced after selection. */
export const MODEL_ACCEPTED_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".pdf",
  ".docx",
  ".xlsx",
  ".csv",
  ".txt",
  ".md",
  ".markdown",
  ".json",
] as const;

/** Value for an <input type="file"> accept attribute. */
export const MODEL_ACCEPT_ATTRIBUTE = MODEL_ACCEPTED_EXTENSIONS.join(",");

/** MIME types matching the extensions above, for server-side checks. */
export const MODEL_ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
  "application/json",
  // Browsers vary on these two; both appear for the same files.
  "text/tab-separated-values",
  "application/csv",
] as const;

/** Extensions people reach for that the model cannot read, with the fix. */
const KNOWN_UNSUPPORTED: Record<string, string> = {
  ".xls": "Save it as .xlsx and attach that.",
  ".doc": "Save it as .docx or PDF and attach that.",
  ".ppt": "Export the slides to PDF and attach that.",
  ".pptx": "Export the slides to PDF and attach that.",
  ".zip": "Attach the individual file you want read, not an archive.",
};

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot).toLowerCase();
}

/**
 * Whether a file can be sent to the model, and if not, why — phrased so the
 * author knows what to do rather than only what went wrong.
 */
export function checkAttachment(file: { name: string; type?: string }): {
  ok: boolean;
  reason?: string;
} {
  const ext = extensionOf(file.name);

  if ((MODEL_ACCEPTED_EXTENSIONS as readonly string[]).includes(ext)) {
    return { ok: true };
  }

  const advice = KNOWN_UNSUPPORTED[ext];
  if (advice) {
    return {
      ok: false,
      reason: `${ext} files can't be read by the model. ${advice}`,
    };
  }

  return {
    ok: false,
    reason:
      `${ext || "That file type"} isn't supported. ` +
      `Attach one of: ${MODEL_ACCEPTED_EXTENSIONS.join(", ")}.`,
  };
}
